/**
 * ModalPanel — centered overlay modal.
 * siesa-ui-kit does not export a Modal component; custom implementation.
 * Focus trap: Escape key triggers onClose.
 *
 * @module components/form/ModalPanel
 * @see FMPV-MVWC-E003-S001 I-002
 */
import React, { type ReactNode, useEffect, useRef } from 'react';

export interface ModalPanelProps {
  isOpen: boolean;
  children: ReactNode;
  onClose: () => void;
  /** Custom modal width. Accepts any valid CSS value (e.g. '800px', '70vw'). Default: '640px' */
  width?: string;
  /** Custom modal max-height. Accepts any valid CSS value (e.g. '600px', '80vh'). Default: '90vh' */
  maxHeight?: string;
}

export function ModalPanel({ isOpen, children, onClose, width = '640px', maxHeight = '90vh' }: ModalPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="absolute inset-0 bg-black/40 z-50 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={panelRef}
        data-testid="modal-panel"
        className="bg-background-secondary dark:bg-dark-bg-secondary rounded-lg shadow-2xl flex flex-col overflow-hidden"
        style={{ width, maxHeight }}
      >
        {children}
      </div>
    </div>
  );
}
